import React, {createContext, ReactNode, useEffect, useState} from 'react';

import RewardShopService from '../services/RewardShopService';
import usePrevious from '../utils/customHooks';
import {BonusPointsOverview, RewardShopArticle, RewardShopServiceData, View} from '../utils/types';
import useToken from '../utils/useToken';
import Constants from "../Constants";
import MetricsService from "../services/MetricsService";
import {Domain, findSiteByDomain} from "@zooplus/commons-lang";
import {getLang} from "../utils/locale";

interface RewardShopContextProps {
  bonusPointsOverview: BonusPointsOverview;
  allArticles: RewardShopArticle[];
  chosenArticleIds: number[];
  giftAmount: number;
  giftStatus: boolean | undefined;
  hasAnyRedemption: boolean;
  onSendingMetricEvent: (method: string) => void;
}

const RewardShopContext = createContext<Partial<RewardShopContextProps>>({});

export interface RewardShopProviderProps {
  children: ReactNode;
  view: View;
  siteDomain: Domain;
  metricsService: MetricsService;
}

export const RewardShopProvider: React.FC<RewardShopProviderProps> = (
  { children, view, siteDomain, metricsService }): JSX.Element => {
  const token = view === 'private' ? useToken().token : undefined;
  const [checkToken, setCheckToken] = useState<boolean>(false);
  const [bonusPointsOverview, setBonusPointsOverview] = useState<BonusPointsOverview>();
  const [allArticles, setAllArticles] = useState<RewardShopArticle[]>();
  const [chosenArticleIds, setChosenArticleIds] = useState<number[]>();
  const [giftAmount, setGiftAmount] = useState<number>();
  const [giftStatus, setGiftStatus] = useState<boolean | undefined>(undefined);
  const [hasAnyRedemption, setHasAnyRedemption] = useState<boolean>(false);
  const [rewardShopData, setRewardShopData] = useState<RewardShopServiceData | null>(null);
  const rewardShopService = new RewardShopService(metricsService);
  const previousToken = usePrevious(token);
  const site = findSiteByDomain(siteDomain);
  const updateRewardShopOverview = (): void => {
    if (view === 'public' || token) {
      rewardShopService
        .onGetRewardShopOverview(token, siteDomain, getLang(site))
        .then(setRewardShopData);
    }
  };

  const onSendingMetricEvent = (method: string): void => {
    (metricsService as any)[method]();
  };

  useEffect(() => {
    if (rewardShopData) {
      setAllArticles(rewardShopData.articles);
      if (view === 'private') {
        setBonusPointsOverview(rewardShopData.bonusPointsOverview);
        setChosenArticleIds(rewardShopData.chosenArticleIds);
        setGiftAmount(rewardShopData.giftAmount);
        setGiftStatus(rewardShopData.giftStatus);
        setHasAnyRedemption(rewardShopData.hasAnyRedemption ?? false);
      }
    }
  }, [rewardShopData]);

  useEffect(() => {
    const checkToken = setTimeout(() => {
      setCheckToken(true);
    }, Constants.RENDER_TIMEOUT);
    return () => clearTimeout(checkToken);
  }, []);

  useEffect(() => {
    if (checkToken && !token && view === 'private') {
      onSendingMetricEvent('tokenError');
    }
  }, [checkToken]);

  useEffect(() => {
    if (!previousToken) {
      if (view === 'private' && token) {
        onSendingMetricEvent('firstEventToken');
      }
      if (!rewardShopData) updateRewardShopOverview();
    }
  }, [token]);

  return (
    <RewardShopContext.Provider
      value={{
        bonusPointsOverview,
        allArticles,
        chosenArticleIds,
        giftAmount,
        giftStatus,
        hasAnyRedemption,
        onSendingMetricEvent,
      }}>
      {children}
    </RewardShopContext.Provider>
  );
};

export default RewardShopContext;
